import { ChipletButton, SlideContent } from "@/types/types";
import { modalController } from "@ionic/vue";
import { createAnimation } from "@ionic/core";
import ModalWithSlides from "@/components/ModalWithSlides.vue";
import {openAlert} from "@/views/Calculator/CalculatorCommon";
import {getVideoDataUri} from "@/utilities/Compatibility";
import { AttachedResource } from "@/storage/DataStorage";
import ResourceViewerModal from "@/components/ResourceViewerModal.vue";
import MenuModal from "@/components/MenuModal.vue";

async function showModalWithSlides(slideContents: SlideContent[]) {
    const modal = await createModalWithSlides(slideContents);
    await modal.present();
}

async function showOnboardingModalWithSlides(slideContents: SlideContent[], dismiss: () => any) {
    const modal = await createOnboardingModalWithSlides(slideContents, dismiss);
    await modal.present();
}

async function showResourceViewerModal(resource: AttachedResource) {
    const modal = await createResourceViewerModal(resource);
    await modal.present();
}

async function showMenuModal(menuItems:ChipletButton[] , menuClass: string , menuTitle: string){
    const modal = await createMenuModal(menuItems,menuClass,menuTitle);
    await modal.present();
}

async function createModalWithSlides(slideContents: SlideContent[]) {
    return await modalController.create({
        mode: "md",
        component: ModalWithSlides,
        cssClass: 'custom-modal',
        componentProps: {
            dismiss: async () => await modalController.dismiss(),
            slideContents
        },
    });
}

async function createMenuModal(menuItems: ChipletButton[] ,menuClass: string , menuTitle: string){
    

    const enterAnimation = (baseEl: HTMLElement) => {
        const backdropAnimation = createAnimation()
          .addElement(baseEl.querySelector("ion-backdrop")!)
          .fromTo("opacity", "0", "var(--backdrop-opacity)");
      
        const wrapperAnimation = createAnimation()
          .addElement(baseEl.querySelector(".modal-wrapper")!)
          .fromTo("transform", "translateY(100%)", "translateY(0%)")
          .fromTo("opacity", "0", "1");
      
        return createAnimation()
          .addElement(baseEl)
          .easing("ease-out")
          .duration(300)
          .addAnimation([backdropAnimation, wrapperAnimation]);
      };
      
    const leaveAnimation = (baseEl: HTMLElement) => {
        const backdropAnimation = createAnimation()
          .addElement(baseEl.querySelector("ion-backdrop")!)
          .fromTo("opacity", "var(--backdrop-opacity)", "0");
      
        const wrapperAnimation = createAnimation()
          .addElement(baseEl.querySelector(".modal-wrapper")!)
          .fromTo("transform", "translateY(0%)", "translateY(100%)")
          .fromTo("opacity", "1", "0");
      
        return createAnimation()
          .addElement(baseEl)
          .easing("ease-in")
          .duration(300)
          .addAnimation([backdropAnimation, wrapperAnimation]);
      };
   
    return await modalController.create({
        mode: "md",
        id:"menu-modal",
        component:MenuModal,
        cssClass: menuClass,
        componentProps: {
            dismiss: async () => await modalController.dismiss(),
            menuItems,
            menuClass,
            
        },
        enterAnimation,
        leaveAnimation,
    });
}

async function createResourceViewerModal(resource: AttachedResource){
    let modalClass = '';
    switch(resource.MimeType){
        case "audio/mpeg":
            modalClass = 'custom-modal-audioplayer';
            break;
        case "text/html":
        case "text/x-uri":
            modalClass = 'custom-modal-iframe';
            break;
        default:
            modalClass = 'custom-modal-pdfviewer';
    }
    const enterAnimation = (baseEl: HTMLElement) => {
        const backdropAnimation = createAnimation()
          .addElement(baseEl.querySelector("ion-backdrop")!)
          .fromTo("opacity", "0", "var(--backdrop-opacity)");
      
        const wrapperAnimation = createAnimation()
          .addElement(baseEl.querySelector(".modal-wrapper")!)
          .fromTo("transform", "translateY(100%)", "translateY(0%)")
          .fromTo("opacity", "0", "1");
      
        return createAnimation()
          .addElement(baseEl)
          .easing("ease-out")
          .duration(300)
          .addAnimation([backdropAnimation, wrapperAnimation]);
      };
      
      const leaveAnimation = (baseEl: HTMLElement) => {
        const backdropAnimation = createAnimation()
          .addElement(baseEl.querySelector("ion-backdrop")!)
          .fromTo("opacity", "var(--backdrop-opacity)", "0");
      
        const wrapperAnimation = createAnimation()
          .addElement(baseEl.querySelector(".modal-wrapper")!)
          .fromTo("transform", "translateY(0%)", "translateY(100%)")
          .fromTo("opacity", "1", "0");
      
        return createAnimation()
          .addElement(baseEl)
          .easing("ease-in")
          .duration(300)
          .addAnimation([backdropAnimation, wrapperAnimation]);
      };
    return await modalController.create({
        mode: "md",
        component:ResourceViewerModal,
        cssClass: modalClass,
        componentProps: {
            dismiss: async () => await modalController.dismiss(),
            resource
        },
        enterAnimation,
        leaveAnimation,
    });
}





async function createOnboardingModalWithSlides(slideContents: SlideContent[], dismiss: () => any) {
    const modal = await createModalWithSlides(slideContents);
    modal.componentProps!.dismiss = dismiss;
    modal.componentProps!.shouldShowCalculatorIconOnInstance = false;
    return modal;
}

function getSlideContents(pages: any) {
    return pages.map((page: any) => {
        return {
            imageSrc: getVideoDataUri(page.image),
            contentText: page.contentText,
            audioSrc: page.audioFile
        } as SlideContent;
    });
}

export async function openResourceViewerModal(resource: AttachedResource) {
    await showResourceViewerModal(resource);
}

export async function openMenuModal(menuItems: ChipletButton[],menuClass: string,menuTitle: string){
    await showMenuModal(menuItems,menuClass,menuTitle);
}

export async function openJourneyHelpPagesModal(localeMessages: any) {
    const slideContents = getSlideContents(localeMessages.journeyHelpPages);
    await showModalWithSlides(slideContents);
}

export async function openBigPicturePagesModal(localeMessages: any) {
    const slideContents = getSlideContents(localeMessages.bigPic);
    await showModalWithSlides(slideContents);
}

export async function openLearnWhereToStartModal(localeMessages: any) {
    const slideContents = getSlideContents(localeMessages.learnWhereToStartPages);
    await showModalWithSlides(slideContents);
}

export async function openOnboardingPagesModal(languageData: any) {
    const dismissModal = async () => {
        const { alternatePinScreen, pinScreenOn } = languageData.buildConfigs.config;
        await modalController.dismiss();
        if (alternatePinScreen === "false" && pinScreenOn === "true") {
            await openAlert(
                `<p>${ languageData.calculator.appSecuredMessage}</p> <p>${ languageData.calculator.createPINMessage }</p>`,
                `${ languageData.calculator.next }`
            );
        }
    };
    const slideContents = getSlideContents(languageData.onboardingPages);
    await showOnboardingModalWithSlides(slideContents, dismissModal);
}

